import { TUserGroup, TUserRank } from "MVVM/@types";
import React from "react";
import styled from "styled-components";
import { Box } from "rebass";

import { randomAvatar } from "../../Utilities/Images";
import { ProfileCardWrapper } from "../ProfileCard/Components/ProfileCardWrapper";

import ProBorder from "../../Assets/Avatar/Border/pro.svg";
import PlatinumBorder from "../../Assets/Avatar/Border/platinum.svg";
import PartnerBorder from "../../Assets/Avatar/Border/partner.svg";
import SuperstarBorder from "../../Assets/Avatar/Border/superstar.svg";
import ProPlayerBorder from "../../Assets/Avatar/Border/pro-player.svg";
import EssentialsBorder from "../../Assets/Avatar/Border/essentials.svg";
import { UserBadge } from "../UserBadge";

const getBorderPadding = (size: string) => {
  const baseSize = parseInt(getSizeStyles(size));
  return Math.round(Math.sqrt(baseSize) * 2);
};

interface AvatarProps {
  userGroup?: TUserGroup | undefined;
  rank?: TUserRank | undefined;
  className?: string;
  icon?: boolean;
  style?: React.CSSProperties;
  size?: "xs" | "sm" | "base" | "md" | "lg" | "xl";
  src?: string | null | undefined;
  link?: string | null;
  profileCardUsername?: string;
}

const Avatar = ({
  userGroup = "FREE",
  rank,
  className,
  style,
  size = "base",
  src,
  link,
  profileCardUsername,
}: AvatarProps) => {
  const effectiveUserGroup = getEffectiveUserGroup(userGroup, rank);
  const hasBorder = effectiveUserGroup !== "FREE";

  const renderAvatarContent = () => {
    const avatarSrc = src || randomAvatar();

    const avatar = (
      <AvatarContainer
        $size={size}
        className={className}
        style={style}
        data-testid="avatar"
        hasBorder={hasBorder}
      >
        <AvatarImage
          src={avatarSrc}
          size={size}
          className={className}
          hasBorder={hasBorder}
        />
        {hasBorder && (
          <BorderOverlay userGroup={effectiveUserGroup} size={size} />
        )}
        {hasBorder && (
          <BadgeWrapper size={size} className="badge-wrapper">
            <UserBadge userGroup={effectiveUserGroup} size={size} />
          </BadgeWrapper>
        )}
      </AvatarContainer>
    );

    if (profileCardUsername) {
      return (
        <ProfileCardWrapper username={profileCardUsername} avatar={avatarSrc}>
          {avatar}
        </ProfileCardWrapper>
      );
    }

    return avatar;
  };

  const avatarContent = renderAvatarContent();

  return link ? (
    <StyledLink href={link}>{avatarContent}</StyledLink>
  ) : (
    avatarContent
  );
};

const getSizeStyles = (size: string) => {
  switch (size) {
    case "xs":
      return "24px";
    case "sm":
      return "32px";
    case "base":
    case "md":
      return "40px";
    case "lg":
      return "88px";
    case "xl":
      return "180px";
    default:
      return "40px";
  }
};

export const getEffectiveUserGroup = (
  userGroup: TUserGroup,
  rank?: TUserRank,
): TUserGroup => {
  switch (rank) {
    case "Official Partner":
      return "PARTNER";
    case "Team Allstar":
      return "INTERNAL";
    case "Superstar":
      return "SUPERSTAR";
    default:
      return userGroup;
  }
};

const getBorderSVG = (userGroup: TUserGroup) => {
  switch (userGroup) {
    case "PRO":
      return `url(${ProBorder})`;
    case "PARTNER":
    case "INTERNAL":
      return `url(${PartnerBorder})`;
    case "CREATOR_PLAT":
      return `url(${PlatinumBorder})`;
    case "SUPERSTAR":
      return `url(${SuperstarBorder})`;
    case "PRO_PLAYER":
      return `url(${ProPlayerBorder})`;
    case "ESSENTIALS":
      return `url(${EssentialsBorder})`;
    case "FREE":
    default:
      return "none";
  }
};

const AvatarContainer = styled(Box)<{
  $size: string;
  hasBorder?: boolean;
}>`
  align-items: center;
  cursor: pointer;
  display: flex;
  height: ${({ $size }) => {
    const baseSize = parseInt(getSizeStyles($size));
    const padding = getBorderPadding($size);
    return `${baseSize + padding * 1}px`;
  }};
  justify-content: center;
  position: relative;
  width: ${({ $size }) => {
    const baseSize = parseInt(getSizeStyles($size));
    const padding = getBorderPadding($size);
    return `${baseSize + padding * 1}px`;
  }};
`;

const AvatarImage = styled.img<{
  size: string;
  hasBorder?: boolean;
}>`
  border-radius: 50%;
  height: ${({ size }) => getSizeStyles(size)};
  object-fit: cover;
  width: ${({ size }) => getSizeStyles(size)};
  z-index: 0;
`;

const BorderOverlay = styled(Box)<{
  userGroup: TUserGroup;
  size: string;
}>`
  background-image: ${({ userGroup }) => getBorderSVG(userGroup)};
  background-position: center;
  background-repeat: no-repeat;
  background-size: 100% 100%;
  border-radius: 50%;
  height: 100%;
  left: 0;
  pointer-events: none;
  position: absolute;
  top: 0;
  width: 100%;
`;

const BadgeWrapper = styled(Box)<{ size: string }>`
  bottom: ${({ size }) => {
    const padding = getBorderPadding(size);
    return `${Math.round(padding * 0.1)}px`;
  }};
  position: absolute;
  right: ${({ size }) => {
    const padding = getBorderPadding(size);
    return `${Math.round(padding * 0.1)}px`;
  }};
  z-index: 10;
`;

const StyledLink = styled.a`
  display: inline-block;
  line-height: 0;
  margin: 0;
  padding: 0;
  text-decoration: none;
`;

export { Avatar };
